import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { differenceInMilliseconds } from 'date-fns';
import type { Polygon } from 'geojson';
import { useAtomValue, useSetAtom } from 'jotai';
import { useDispatch } from 'react-redux';
import type { Simplify } from 'type-fest';

import { DataSource } from '../@types/global';
import type { GetPatientLatestImagesResponse } from '../@types/latestImages';
import { selectedVisitAtom } from '../app/appHeader/imageViewerMenu/atoms';
import { updateUserName } from '../modules/annotator/AnnotatorActions';
import {
  fmxVersionAtom,
  latestImagesVisitDateAtom,
} from '../modules/annotator/atoms';
import { getPatientName } from '../modules/uploader/patientList/patientListTableContainer/utils';
import { mixpanelEvents, useTrackEvent } from '../utils/mixpanel';

import { makeAPIRequestReactQuery } from './api';
import { endpoints } from './endpoints';
import { usePatientVisits } from './usePatientVisits';
import { usePreferences } from './usePreferences';
import type { TCariesDepth } from './useSegmentations';

interface UseLatestImagesParams {
  videaPatientId: string;
  externalPatientId?: string;
  fmxVersion?: 1 | 2;
  limit?: number;
  estimatedVisitId?: string | null;
  isInsuranceExport?: boolean;
  options?: UseQueryOptions<
    GetPatientLatestImagesResponse,
    unknown,
    GetPatientLatestImagesResponse
  >;
}

export type TLatestImagesResponse = Simplify<
  ReturnType<typeof useLatestImages>['data']
>;

export const useLatestImages = ({
  videaPatientId,
  externalPatientId,
  limit = 50,
  estimatedVisitId,
  options,
  isInsuranceExport,
}: UseLatestImagesParams) => {
  const { trackEvent } = useTrackEvent();
  const selectedVisit = useAtomValue(selectedVisitAtom);
  // If a visit is passed in, use it. Otherwise the atom will be the default value.
  const visitId = estimatedVisitId || selectedVisit;
  const fmxVersion = useAtomValue(fmxVersionAtom);
  const setLatestImagesVisitDateAtom = useSetAtom(latestImagesVisitDateAtom);
  const dispatch = useDispatch();

  const displayDentalPhotos = usePreferences()?.data?.displayDentalPhotos;
  const visits = usePatientVisits({ patientId: videaPatientId });

  const patientId =
    visits?.data?.visits?.find((visit) => visit.id === Number(visitId))
      ?.patientVideaId || videaPatientId;
  const path = `${endpoints.patient}/${patientId}/${endpoints.latestImages}`;

  // const modelHashes =

  return useQuery<GetPatientLatestImagesResponse>(
    [
      endpoints.patient,
      endpoints.latestImages,
      patientId,
      visitId,
      {
        bwOnly: false,
        fmxVersion,
      },
      isInsuranceExport,
    ],
    () => {
      const priorTime = new Date();
      return makeAPIRequestReactQuery<GetPatientLatestImagesResponse>(path, {
        bwOnly: false,
        limit,
        fmxVersion,
        estimatedVisitId: visitId,
      }).then((resp) => {
        trackEvent(mixpanelEvents.latestImagesResponseTime, {
          loadTime: differenceInMilliseconds(new Date(), priorTime),
          patientId: patientId,
          estimatedVisitId: visitId,
        });
        return mergeMongoAndPgCaries(resp);
      });
    },
    {
      ...options,
      enabled: !!patientId && !!options?.enabled,
      onSuccess: (data) => {
        let filteredData = data;
        if (displayDentalPhotos !== 'on') {
          const images = data.analyzedImages?.filter(
            (i) => i.detectedImageType !== 'COLOR_IMAGE',
          );
          filteredData = {
            ...data,
            analyzedImages: images,
          };
        }
        if (options?.onSuccess) {
          options.onSuccess(filteredData);
        }
        if (filteredData.analyzedImages[0]?.imageAcquiredAt) {
          setLatestImagesVisitDateAtom(
            new Date(filteredData.analyzedImages[0].imageAcquiredAt),
          );
        }
        if (filteredData.patientName) {
          const patientName = getPatientName(filteredData.patientName, 0, true);
          dispatch(updateUserName(patientId, patientName));
        }
      },
      select: (latestImagesResponse: GetPatientLatestImagesResponse) => ({
        ...latestImagesResponse,
        analyzedImages: latestImagesResponse.analyzedImages
          .filter(
            (i) =>
              displayDentalPhotos === 'on' ||
              i.detectedImageType !== 'COLOR_IMAGE',
          )
          .map((image) => ({
            ...image,
            // the old workflow has a few complex functions needing this, and the PPD workflow does not
            // Add this dummy prop for now until the old workflow is removed
            path: image.path,
            patientInfo: {
              patientId: videaPatientId,
              externalPatientId: externalPatientId ?? null,
            },
            // detect uses objectId as a image id in the old VCA workflow.
            // Instead of changing that in multiple places just set the prop here
            objectId: image.imageId,
            dataSource: DataSource.NUCLEUS,
          })),
      }),
    },
  );
};

/**
 * given GetPatientLatestImagesResponse
 * return depth in the mongo caries
 */
function mergeMongoAndPgCaries(resp: GetPatientLatestImagesResponse) {
  const flattenedPgCaries = resp.analyses?.flatMap((a) =>
    a.teeth?.flatMap((t) =>
      t?.caries?.map((caries) => ({
        ...caries,
        coords: extractBoundingBoxFromGeoJSON(caries.bounds),
        mongoImageId: a.mongoImageId,
      })),
    ),
  );
  const zippedAnalyzedImages = resp.analyzedImages?.map((image) => {
    const cariesForImage = flattenedPgCaries?.filter(
      (pgCaries) => pgCaries?.mongoImageId === image.imageId,
    );
    const mongoAnalyses = image.models?.caries?.findings;

    const zippedCariesFindings = mongoAnalyses?.map((mongoCaries) => {
      const matchedPgCaries = cariesForImage?.find((pgCaries) => {
        const pgCoords = pgCaries?.coords;
        return (
          mongoCaries?.xMin.toFixed() === pgCoords?.xMin.toFixed() &&
          mongoCaries?.yMin.toFixed() === pgCoords?.yMin.toFixed() &&
          mongoCaries?.xMax.toFixed() === pgCoords?.xMax.toFixed() &&
          mongoCaries?.yMax.toFixed() === pgCoords?.yMax.toFixed()
        );
      });
      return {
        ...mongoCaries,
        depth: matchedPgCaries?.depth
          ? (matchedPgCaries.depth.trim() as TCariesDepth | null | undefined)
          : mongoCaries.depth,
      };
    });
    if (image.detectedImageType === 'COLOR_IMAGE') {
      return {
        ...image,
        models: {
          ...image.models,
        },
      };
    }
    return {
      ...image,
      models: {
        ...image.models,
        caries: {
          ...image.models?.caries,
          findings: zippedCariesFindings,
        },
      },
    };
  });
  return {
    ...resp,
    analyzedImages: zippedAnalyzedImages,
  };
}

export function extractBoundingBoxFromGeoJSON(polygon: Polygon | null):
  | {
      xMin: number;
      yMin: number;
      xMax: number;
      yMax: number;
    }
  | undefined {
  if (!polygon) {
    return;
  }
  const coordinates = polygon.coordinates;
  let xMin = Infinity,
    yMin = Infinity,
    xMax = -Infinity,
    yMax = -Infinity;

  coordinates.forEach((coord) => {
    xMin = Math.min(xMin, coord[0][0]);
    yMin = Math.min(yMin, coord[0][1]);
    xMax = Math.max(xMax, coord[2][0]);
    yMax = Math.max(yMax, coord[2][1]);
  });

  return { xMin, yMin, xMax, yMax };
}
